import {BrowsePageModel} from './model';
import {action, observable, runInAction} from 'mobx';
import {RedirectFunction, RouterState} from 'react-router';
import {APIController} from '../../api/controller';
import {UIController} from '../../ui/controller';
import {BugsnagController} from '../../bugsnag/controller';
import {ContentModel} from '../../content/model';
import {EnvModel} from '../../env/model';
import {PrismicController} from '../../prismic/controller';
import {ImageController} from '../../image/controller';
import {RouterModel} from "../../router/model";
import {stripQueryString} from '../../../lib/string';
import AppBreadcrumbs from '../../../components/app-breadcrumbs';

export class BrowsePageController {
  constructor(
    private model: BrowsePageModel,
    private api: APIController,
    private ui: UIController,
    private bugsnag: BugsnagController,
    private content: ContentModel,
    private env: EnvModel,
    private prismic: PrismicController,
    private image: ImageController,
    private router: RouterModel,
  ) {}

  @action onEnter = async (nextState: RouterState, replace: RedirectFunction) => {
    this.model.loading = true;
    this.model.type = stripQueryString(nextState.params['type']) || null;
    this.model.slug = stripQueryString(nextState.params['slug']) || null;

    // Need the playlist id from globals
    await this.env.ready;

    runInAction(() => {
      this.model.sections = observable([...this.content.browseSections]);
    });

    this.setBreadcrumbs();

    if (this.model.loadedInitial) {
      return this.setBrowseSEO(nextState.location.pathname);
    }

    const documentId = 'browse';

    try {
      const [playlists, genres, themes, moods, releases, content] = await Promise.all([
        this.api.browse.getBrowsePlaylists(),
        this.api.browse.getSystemTags('hookd_genre'),
        this.api.browse.getSystemTags('hookd_theme'),
        this.api.browse.getSystemTags('hookd_mood'),
        this.api.browse.getRightsholderReleases(this.content.browseBottomGridRightsholder),
        this.prismic.getSingle(documentId),
        this.setBrowseSEO(nextState.location.pathname),
        this.env.ready
      ]);

      const fallbackImage = themes.data.find(t => !!t.images && !!t.images.identity);

      let playlist, ogImageIdentity;

      if (this.model.slug !== null) {
        playlist = await this.api.browse.getPlaylist(this.model.slug);
      }

      runInAction(() => {
        this.model.playlists = playlists.data;
        this.model.genres = genres.data;
        this.model.releases = releases.data;
        this.model.themes = themes.data;
        this.model.moods = moods.data;

        const heroSlice = content.data.body.find((slice: any) => slice.slice_type === 'hero_artist');

        this.model.heroArtists = (heroSlice && heroSlice.items || []).map((artist: any) => ({
          name: artist.name,
          image: artist.image.url,
          slug: artist.slug
        }));

        if (typeof playlist != "undefined" && playlist !== null) {
          ogImageIdentity = playlist.data.images.identity;
        } else {
          ogImageIdentity = this.model.heroArtists[0].image;
        }

        this.model.selectedStaffPick = playlists.data[0] ? playlists.data[0].slug : null;
        this.model.selectedGenre = genres.data[0] ? genres.data[0].slug : null;
        this.model.selectedTheme = themes.data[0] ? themes.data[0].slug : null;
        this.ui.setOpenGraph({
          image: this.image.preloadImage(ogImageIdentity, { width: 1200, height: 630 })
        });

        this.model.loadedInitial = true;
      });
    } catch(e) {
      this.bugsnag.notifyException(e);
    } finally {
      runInAction(() => {
        this.model.loading = false;
      })
    }
  };

  @action onEnterTag = async (nextState: RouterState, replace: RedirectFunction) => {
    this.model.loading = true;

    const page = parseInt(stripQueryString(nextState.params['page'])) || 1;
    this.model.from = (page - 1) * this.model.size;

    return this.onEnter(nextState, replace);
  };

  @action goToPage = (page: number) => {
    const from = (page - 1) * this.model.size;

    this.model.from = from;
    this.router.push(this.model.activePath(page));
  };

  @action selectStaffPick = (slug: string) => this.model.selectedStaffPick = slug;
  @action selectGenre = (slug: string) => this.model.selectedGenre = slug;
  @action selectTheme = (slug: string) => this.model.selectedTheme = slug;

  @action private setBreadcrumbs = (label?: string) => {
    const {type, slug} = this.model;

    const breadcrumbs = [
      {
        path: '/browse',
        label: 'Browse'
      },
    ];

    if (type) breadcrumbs.push({
      path: `/browse/${type}`,
      label: this.model.typeLabel,
    });

    if (slug) breadcrumbs.push({
      path: `/browse/${type}/${slug}`,
      label: label || AppBreadcrumbs.LOADING
    });

    this.ui.setBreadcrumbs(breadcrumbs);
  };

  @action public setBrowseSEO = async (path: string) => {
    const {setSEO} = this.ui;
    const {slug, type, title} = this.model;

    if (!type)
      return setSEO(path);

    return setSEO(
      !!slug ? `/browse/${type}/slug` : `/browse/${type}`,
      {[type]: title}
    );
  }
}